import { useEffect, useRef } from 'react'

/**
 * Runs `onResize` whenever the observed element changes size.
 *
 * Watches the element, not the ref holding it. A RefObject's identity never changes, so an effect
 * keyed on it runs once, at a commit where the element may not be mounted yet, and never again. This
 * runs after every commit and compares the current element against the observed one, which costs an
 * identity check when nothing moved and re-observes when a caller mounts its element late or swaps
 * it for another.
 *
 * `onResize` is read from a ref, so a caller does not have to memoize it.
 */
export function useResizeObserver(target: React.RefObject<Element | null>, onResize: () => void): void {
	const onResizeRef = useRef(onResize)
	onResizeRef.current = onResize
	const observedRef = useRef<Element | null>(null)
	const observerRef = useRef<ResizeObserver | null>(null)

	useEffect(() => {
		const element = target.current
		if (element === observedRef.current) {
			return
		}
		observerRef.current?.disconnect()
		observedRef.current = element
		if (!element) {
			observerRef.current = null
			return
		}
		const observer = new ResizeObserver(() => onResizeRef.current())
		observer.observe(element)
		observerRef.current = observer
	})

	useEffect(() => {
		return () => {
			observerRef.current?.disconnect()
			observerRef.current = null
			observedRef.current = null
		}
	}, [])
}
